import { useBurnNFTMutation } from "@/api/inventory/burn";
import { Button, Group, NumberInput, Stack, Text } from "@mantine/core";
import { IconReload } from "@tabler/icons-react";
import { useForm, useStore } from "@tanstack/react-form";
import { z } from "zod";

const formSchema = z.object({
	tokenId: z.number().min(0, "Token ID must be positive"),
});

type FormValues = z.infer<typeof formSchema>;

const DEFAULT_VALUES: FormValues = {
	tokenId: 0,
};

interface ERC721ABurnProps {
	collectionAddress: string;
}

export function ERC721ABurn({ collectionAddress }: ERC721ABurnProps) {
	const burnMutation = useBurnNFTMutation();

	const form = useForm({
		defaultValues: DEFAULT_VALUES,
		validators: {
			onChange: formSchema,
		},
		onSubmit: async ({ value }) => {
			await burnMutation.mutateAsync({
				collectionAddress,
				tokenId: value.tokenId,
			});
		},
	});

	const canSubmit = useStore(form.store, (state) => state.canSubmit);
	const isSubmitting = burnMutation.isPending;
	const isSubmitted = burnMutation.isSuccess;

	return (
		<Stack gap="md" p="md" maw={900} mx="auto">
			<Group justify="space-between">
				<Text size="xl" fw={700}>
					Burn NFT (ERC721A)
				</Text>
				<Group gap="sm">
					<Button
						variant="subtle"
						leftSection={<IconReload size="1.2rem" />}
						onClick={() => {
							form.reset();
							burnMutation.reset();
						}}
					>
						Reset Form
					</Button>
					{isSubmitted ? (
						<Button
							onClick={() => {
								burnMutation.reset();
							}}
						>
							Burn Another
						</Button>
					) : (
						<Button
							onClick={() => form.handleSubmit()}
							loading={isSubmitting}
							disabled={!canSubmit || isSubmitting || isSubmitted}
						>
							Burn NFT
						</Button>
					)}
				</Group>
			</Group>

			<Stack gap="md">
				<form.Field name="tokenId">
					{(field) => (
						<NumberInput
							label="Token ID"
							placeholder="Enter token ID"
							value={field.state.value ?? undefined}
							onChange={(value) =>
								field.handleChange(value === "" ? 0 : Number(value))
							}
							onBlur={field.handleBlur}
							min={0}
							required
							allowDecimal={false}
							step={1}
							error={field.state.meta.errors?.[0]?.message}
						/>
					)}
				</form.Field>
			</Stack>
		</Stack>
	);
}
